import { Box, Container, Typography, Stack, Grid, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useLanguage } from '../contexts/LanguageContext';
import SEOHead from '../components/SEOHead';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import PhoneAndroidOutlinedIcon from '@mui/icons-material/PhoneAndroidOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';

const PageSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(12, 0),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(8, 0),
  },
}));

const GlassCard = styled(Box)(({ theme }) => ({
  background: theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.05)'
    : 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(20px)',
  borderRadius: '24px',
  padding: theme.spacing(4),
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
  boxShadow: theme.palette.mode === 'dark'
    ? '0 8px 32px rgba(0, 0, 0, 0.3)'
    : '0 8px 32px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  height: '100%',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 12px 48px rgba(0, 0, 0, 0.4)'
      : '0 12px 48px rgba(0, 0, 0, 0.15)',
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: '80px',
  height: '80px',
  borderRadius: '20px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  marginBottom: theme.spacing(3),
  boxShadow: theme.palette.mode === 'dark'
    ? '0 8px 24px rgba(16, 185, 129, 0.3)'
    : '0 8px 24px rgba(16, 185, 129, 0.25)',
  '& svg': {
    fontSize: '2.5rem',
    color: '#FFFFFF',
  },
}));

const ProcessStep = styled(Box)(({ theme }) => ({
  position: 'relative',
  paddingLeft: theme.spacing(4),
  paddingBottom: theme.spacing(4),
  '&::before': {
    content: '""',
    position: 'absolute',
    left: '15px',
    top: '32px',
    bottom: '0',
    width: '2px',
    background: `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  },
  '&:last-child::before': {
    display: 'none',
  },
}));

const StepNumber = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: 0,
  top: '16px',
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  color: '#FFFFFF',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
  fontSize: '0.875rem',
  boxShadow: `0 0 0 4px ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
}));

export default function SoftwareDevelopment() {
  const { t } = useLanguage();

  const technologies = [
    {
      icon: <CodeOutlinedIcon />,
      title: t('service.software.tech.web'),
      desc: t('service.software.tech.web.desc'),
    },
    {
      icon: <PhoneAndroidOutlinedIcon />,
      title: t('service.software.tech.mobile'),
      desc: t('service.software.tech.mobile.desc'),
    },
    {
      icon: <DnsOutlinedIcon />,
      title: t('service.software.tech.backend'),
      desc: t('service.software.tech.backend.desc'),
    },
    {
      icon: <StorageOutlinedIcon />,
      title: t('service.software.tech.database'),
      desc: t('service.software.tech.database.desc'),
    },
  ];

  const process = [
    { step: 1, title: t('service.software.process.1'), desc: t('service.software.process.1.desc') },
    { step: 2, title: t('service.software.process.2'), desc: t('service.software.process.2.desc') },
    { step: 3, title: t('service.software.process.3'), desc: t('service.software.process.3.desc') },
    { step: 4, title: t('service.software.process.4'), desc: t('service.software.process.4.desc') },
    { step: 5, title: t('service.software.process.5'), desc: t('service.software.process.5.desc') },
    { step: 6, title: t('service.software.process.6'), desc: t('service.software.process.6.desc') },
  ];

  const benefits = [
    {
      icon: <SpeedOutlinedIcon />,
      title: t('service.software.benefit.1'),
      desc: t('service.software.benefit.1.desc'),
    },
    {
      icon: <TrendingUpOutlinedIcon />,
      title: t('service.software.benefit.2'),
      desc: t('service.software.benefit.2.desc'),
    },
    {
      icon: <SecurityOutlinedIcon />,
      title: t('service.software.benefit.3'),
      desc: t('service.software.benefit.3.desc'),
    },
    {
      icon: <SupportAgentOutlinedIcon />,
      title: t('service.software.benefit.4'),
      desc: t('service.software.benefit.4.desc'),
    },
  ];

  return (
    <>
      <SEOHead titleKey="service.software.title" descriptionKey="service.software.hero.desc" />
      <Box component="main" role="main">
        {/* Hero Section */}
        <PageSection
          sx={{
            background: (theme) =>
              theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
                : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            pt: { xs: 16, md: 20 },
          }}
        >
          <Container maxWidth="lg">
            <Stack spacing={3} alignItems="center" textAlign="center">
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  fontWeight: 700,
                  background: (theme) =>
                    `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {t('service.software.title')}
              </Typography>
              <Typography
                variant="h5"
                color="text.secondary"
                sx={{ maxWidth: '800px', lineHeight: 1.8, fontSize: '1.25rem' }}
              >
                {t('service.software.subtitle')}
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ maxWidth: '900px', lineHeight: 1.8, fontSize: '1.1rem', mt: 2 }}
              >
                {t('service.software.hero.desc')}
              </Typography>
            </Stack>
          </Container>
        </PageSection>

        {/* Technologies Section */}
        <PageSection>
          <Container maxWidth="lg">
            <Stack spacing={6}>
              <Stack spacing={2} alignItems="center" textAlign="center">
                <Typography variant="h2" fontWeight={700}>
                  {t('service.software.technologies.title')}
                </Typography>
              </Stack>
              <Grid container spacing={4}>
                {technologies.map((tech, index) => (
                  <Grid item xs={12} sm={6} md={3} key={index}>
                    <GlassCard>
                      <Stack spacing={2} alignItems="center" textAlign="center">
                        <IconWrapper>{tech.icon}</IconWrapper>
                        <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.1rem' }}>
                          {tech.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                          {tech.desc}
                        </Typography>
                      </Stack>
                    </GlassCard>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </Container>
        </PageSection>

        {/* Process Section */}
        <PageSection
          sx={{
            background: (theme) =>
              theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.02)'
                : 'rgba(0, 0, 0, 0.02)',
          }}
        >
          <Container maxWidth="lg">
            <Stack spacing={6}>
              <Stack spacing={2} alignItems="center" textAlign="center">
                <Typography variant="h2" fontWeight={700}>
                  {t('service.software.process.title')}
                </Typography>
              </Stack>
              <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
                {process.map((item) => (
                  <ProcessStep key={item.step}>
                    <StepNumber>{item.step}</StepNumber>
                    <GlassCard>
                      <Typography
                        variant="h6"
                        color="primary"
                        fontWeight={700}
                        gutterBottom
                        sx={{ fontSize: '1.1rem' }}
                      >
                        {item.title}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
                        {item.desc}
                      </Typography>
                    </GlassCard>
                  </ProcessStep>
                ))}
              </Box>
            </Stack>
          </Container>
        </PageSection>

        {/* Benefits Section */}
        <PageSection>
          <Container maxWidth="lg">
            <Stack spacing={6}>
              <Stack spacing={2} alignItems="center" textAlign="center">
                <Typography variant="h2" fontWeight={700}>
                  {t('service.software.benefits.title')}
                </Typography>
              </Stack>
              <Grid container spacing={4}>
                {benefits.map((benefit, index) => (
                  <Grid item xs={12} sm={6} md={3} key={index}>
                    <GlassCard>
                      <Stack spacing={2} alignItems="center" textAlign="center">
                        <IconWrapper>{benefit.icon}</IconWrapper>
                        <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.1rem' }}>
                          {benefit.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                          {benefit.desc}
                        </Typography>
                      </Stack>
                    </GlassCard>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </Container>
        </PageSection>

        {/* CTA Section */}
        <PageSection
          sx={{
            background: (theme) =>
              theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
                : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          }}
        >
          <Container maxWidth="lg">
            <GlassCard sx={{ textAlign: 'center', py: 8 }}>
              <Stack spacing={3} alignItems="center">
                <Typography variant="h3" fontWeight={700}>
                  {t('service.cta.title')}
                </Typography>
                <Typography
                  variant="h6"
                  color="text.secondary"
                  sx={{ maxWidth: '600px', fontSize: '1.1rem' }}
                >
                  {t('service.cta.subtitle')}
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  href="https://calendly.com/teknolobi"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    px: 6,
                    py: 2,
                    fontSize: '1.1rem',
                    borderRadius: '12px',
                    background: (theme) =>
                      `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: (theme) =>
                        theme.palette.mode === 'dark'
                          ? '0 12px 40px rgba(0, 0, 0, 0.4)'
                          : '0 12px 40px rgba(0, 0, 0, 0.2)',
                    },
                  }}
                >
                  {t('service.cta.button')}
                </Button>
              </Stack>
            </GlassCard>
          </Container>
        </PageSection>
      </Box>
    </>
  );
}